import { useCallback, useMemo } from 'react';

import {
  VendorTaxonomyKey,
  VENUES_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import ConfettiIcon from '@zola/zola-ui/src/assets/images/icons/celebratory_2.svg';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { WhiteCircleIcon } from '@zola/zola-ui/src/components/CircleIcon/CircleIcon';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';

import cx from 'classnames';

import CachedImage from '~/components/common/images/CachedImage';
import { useCtaTracking } from '~/hooks/useCtaTracking';
import { VendorCardView } from '~/types/responseTypes';
import { formatLocation } from '~/util/formatters';
import { getCanonicalUrl, getPublicReviewUrl } from '~/util/storefrontUrl';
import { CtaType, TrackingProductLocation } from '~/util/trackingHelper';
import { getVendorIcon } from '~/util/vendorUtils';

import { CrossSellWebsite, CROSS_SELL_WEBSITE } from './components/CrossSellWebsite';
import { getVendorTypeName } from './helpers';

import styles from './bookedTile.module.less';

const getTrackingProps = () => ({
  business_unit: 'MARKETPLACE',
  location: /booked$/i.test(window.location.pathname)
    ? TrackingProductLocation.BOOKED_VENDORS_PAGE
    : TrackingProductLocation.EXPLORE_VENDORS_PLP,
  cta_id: 'Cross Sell Website',
  cta: CROSS_SELL_WEBSITE,
  cta_type: CtaType.LINK,
  group_id: 'Cross Sell',
  position: 1,
  section: 'MARKETING',
});

export interface BookedTileProps {
  vendorType: VendorTaxonomyKey;
  vendor: VendorCardView | null;
  review?: boolean;
  simple?: boolean;
  vendorName?: string | null;
  position: number;
}

const BookedTile = ({
  vendorType,
  vendor,
  review,
  vendorName,
  simple,
  position,
}: BookedTileProps): JSX.Element => {
  const Icon = useCallback(() => {
    return (
      <WhiteCircleIcon
        className={styles.icon}
        image={`${getVendorIcon(vendorType, true)}?w=100`}
        alt={`${getVendorTypeName(vendorType)} illustration`}
      />
    );
  }, [vendorType]);

  const VendorImage = useCallback(() => {
    const name = vendor?.vendorName || vendorName;
    return vendor?.storefrontCoverId && name ? (
      <CachedImage
        uuid={vendor.storefrontCoverId}
        srcSizes={[
          { width: 200, height: 150 },
          { width: 250, height: 188 },
          { width: 300, height: 225 },
          { width: 400, height: 300 },
          { width: 500, height: 375 },
          { width: 600, height: 450 },
        ]}
        alt={name}
      />
    ) : (
      <img
        className={styles.defaultImage}
        src={ConfettiIcon}
        alt="Confetti illustration"
        width="85"
        height="85"
      />
    );
  }, [vendor, vendorName]);

  const VendorName = useCallback(() => {
    const key = vendor?.taxonomyNode?.key;
    const slug = vendor?.storefrontSlug;
    const name = vendor?.vendorName || vendorName;
    if (key && slug && name && !simple) {
      const to = getCanonicalUrl(key, slug);
      return (
        <LinkV2 href={to} variant="primary" noTextTransform>
          {name}
        </LinkV2>
      );
    }
    return <div className={styles.vendorName}>{name}</div>;
  }, [vendor, vendorName, simple]);

  const VendorLocation = useCallback(() => {
    const { city } = vendor || {};
    const state = vendor?.stateProvince;
    if (city && state) {
      return (
        <div className={styles.location}>{formatLocation({ city, stateProvince: state })}</div>
      );
    }
    return null;
  }, [vendor]);

  const hed = useMemo(() => {
    const vendorTypeName = getVendorTypeName(vendorType);
    return <p className={styles.nonLink}>{vendorTypeName}</p>;
  }, [vendorType]);

  const { inViewportRef, handleClick } = useCtaTracking<HTMLDivElement>({
    ...getTrackingProps(),
    ...{ position },
  });

  const crossSell = (
    <div className={styles.crossSellContainer} ref={inViewportRef}>
      {!review && vendorType === VENUES_TAXONOMY_KEY && (
        <CrossSellWebsite handleClick={handleClick} />
      )}
    </div>
  );

  return (
    <div
      className={cx(
        styles.bookedTile,
        { [styles.simpleTile]: simple },
        crossSell ? styles.withCrossSell : null
      )}
      data-testid="vendor-team-tile-booked"
    >
      <div className={cx(styles.main, crossSell ? styles.noOverflow : null)}>
        <div className={styles.header}>
          {!simple && <Icon />}
          <div className={styles.headerText}>
            <div className={styles.hed}>{hed}</div>
          </div>
        </div>
        <div className={styles.image}>
          <VendorImage />
        </div>
        <div className={cx(styles.footer)}>
          <div className={styles.booked}>You booked:</div>
          <div className={styles.vendor}>
            <VendorName />
          </div>
          <VendorLocation />
        </div>
        {review && vendor?.storefrontUuid && (
          <ButtonV2
            className={styles.button}
            variant="primary"
            component="a"
            href={getPublicReviewUrl(vendor.storefrontUuid, window.location.href)}
          >
            Review
          </ButtonV2>
        )}
        {crossSell}
      </div>
    </div>
  );
};

export default BookedTile;
